import React from 'react';
import { SessionData } from '../types';
import { hasRaceStarted, formatCountdown, getCountdownColor } from '../utils';
import Button from './ui/Button';

export interface SessionNavigationBarProps {
  hasSearchResults: boolean;
  activeSessionId: string | null;
  savedSessions: SessionData[];
  visibleSessionIds: string[];
  toggleMode: 'static' | 'auto' | 'live';
  onStartNewSearch: () => void;
  onSaveCurrentSession?: () => void;
  onToggleAllSessions: () => void;
  onSwitchToSession: (sessionId: string) => void;
}

const SessionNavigationBar: React.FC<SessionNavigationBarProps> = ({
  hasSearchResults,
  activeSessionId,
  savedSessions,
  visibleSessionIds,
  toggleMode,
  onStartNewSearch,
  onSaveCurrentSession,
  onToggleAllSessions,
  onSwitchToSession,
}) => {
  if (savedSessions.length === 0 && !hasSearchResults) {
    return null;
  }

  const getEVDisplay = (evValue: number | null | undefined) => {
    if (evValue !== null && evValue !== undefined) {
      return `${evValue > 0 ? '+' : ''}${evValue}%`;
    }
    return 'N/A';
  };

  const getEVClass = (evValue: number | null | undefined) => {
    if (evValue === null || evValue === undefined)
      return 'text-text-secondary-light dark:text-text-secondary-dark';
    return evValue > 0 ? 'text-emerald-500' : 'text-red-500';
  };

  return (
    <div className='mb-6'>
      <div className='flex flex-wrap gap-2 mb-4 p-2 bg-surface-light/80 dark:bg-surface-dark/80 backdrop-blur-sm rounded-lg shadow border border-border-light dark:border-border-dark'>
        <Button onClick={onStartNewSearch} variant='blue'>
          New Search
        </Button>
        {hasSearchResults && !activeSessionId && onSaveCurrentSession && (
          <Button onClick={onSaveCurrentSession} variant='blue'>
            Add to Watchlist
          </Button>
        )}
        {savedSessions.length > 0 && (
          <Button onClick={onToggleAllSessions} variant='secondary'>
            {visibleSessionIds.length === savedSessions.length &&
            savedSessions.length > 0
              ? 'Hide All'
              : 'View All'}{' '}
            Sessions
          </Button>
        )}
      </div>

      {savedSessions.length > 0 && (
        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3'>
          {savedSessions.map((session, index) => {
            // Added index for isEvenRow
            const isActive = session.id === activeSessionId;
            const isVisible = visibleSessionIds.includes(session.id);

            const latestEV = session.evHistory?.[session.evHistory.length - 1];
            const latestBackOddsEV =
              session.backOddsHistory?.[session.backOddsHistory.length - 1]
                ?.adjustedEV;

            const countdown = session.results.length
              ? (() => {
                  const nextRace = session.results
                    .filter((r) => !hasRaceStarted(r.marketStartTime))
                    .sort(
                      (a, b) =>
                        new Date(a.marketStartTime).getTime() -
                        new Date(b.marketStartTime).getTime()
                    )[0];
                  return nextRace
                    ? formatCountdown(nextRace.marketStartTime)
                    : {
                        text: 'Ended',
                        className:
                          'text-text-secondary-light dark:text-text-secondary-dark',
                      };
                })()
              : {
                  text: 'No Races',
                  className:
                    'text-text-secondary-light dark:text-text-secondary-dark',
                };

            const countdownColor = session.results.length
              ? (() => {
                  const nextRace = session.results
                    .filter((r) => !hasRaceStarted(r.marketStartTime))
                    .sort(
                      (a, b) =>
                        new Date(a.marketStartTime).getTime() -
                        new Date(b.marketStartTime).getTime()
                    )[0];
                  return nextRace
                    ? getCountdownColor(nextRace.marketStartTime)
                    : { color: '', className: '' };
                })()
              : { color: '', className: '' };

            return (
              <div
                key={session.id}
                onClick={() => onSwitchToSession(session.id)}
                className={`
                  p-3 rounded-lg cursor-pointer transition-all duration-200 ease-in-out
                  border backdrop-blur-sm
                  ${isActive ? 'border-text-secondary-light bg-hover-light/80 dark:border-text-secondary-dark dark:bg-hover-dark/80 shadow-lg scale-105' : 'shadow-md hover:shadow-lg border-border-light dark:border-border-dark hover:border-text-secondary-light dark:hover:border-text-secondary-dark'}
                  ${isVisible ? 'opacity-100' : 'opacity-60 hover:opacity-100'}
                  ${isEvenRowHelper(index) ? 'bg-surface-light/80 dark:bg-surface-dark/80' : 'bg-background-light/80 dark:bg-background-dark/80'}
                `}
                title={`Jockeys: ${session.jockey1}${session.jockey2 ? ' / ' + session.jockey2 : ''}, Odds: ${session.boostedOdds}, Races: ${session.racesToWin}`}
              >
                <div className='flex justify-between items-center mb-1'>
                  <p
                    className='text-xs font-semibold truncate text-text-primary-light dark:text-text-primary-dark'
                    style={{ maxWidth: 'calc(100% - 50px)' }}
                  >
                    {session.jockey1}
                    {session.jockey2 && ` / ${session.jockey2}`}
                  </p>
                  <p
                    className={`text-xs flex-shrink-0 font-mono ${countdown.className} ${countdownColor.className}`}
                    style={{ color: countdownColor.color || undefined }}
                  >
                    {countdown.text}
                  </p>
                </div>
                <div className='text-xxs space-y-0.5'>
                  <div className='flex justify-between'>
                    <span className='text-text-secondary-light dark:text-text-secondary-dark'>
                      Lay EV:
                    </span>
                    <span className={getEVClass(latestEV?.layEV)}>
                      {getEVDisplay(latestEV?.layEV)}
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-text-secondary-light dark:text-text-secondary-dark'>
                      Mid EV:
                    </span>
                    <span className={getEVClass(latestEV?.midpointEV)}>
                      {getEVDisplay(latestEV?.midpointEV)}
                    </span>
                  </div>
                  {session.applyBackOddsPercentage && (
                    <div className='flex justify-between'>
                      <span className='text-text-secondary-light dark:text-text-secondary-dark'>
                        +{session.backOddsPercentage}% EV:
                      </span>
                      <span className={getEVClass(latestBackOddsEV)}>
                        {getEVDisplay(latestBackOddsEV)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const isEvenRowHelper = (index: number) => index % 2 === 0;

export default SessionNavigationBar;
